import { useMemo, useState } from 'react';
import { classifyType, formatCell, isSqlNull, renderableText, toSqlLiteral } from '@/lib/values';
import { copyText } from '@/lib/clipboard';
import type { CellValue, ColumnSchema } from '@/types/api';
import { JsonTree } from './JsonTree';

export interface CellDetailProps {
  readonly column: ColumnSchema;
  readonly value: CellValue;
  readonly rowIndex: number;
  readonly onClose: () => void;
}

/** Parses a nested value that arrived as JSON text from the cache. */
function parseNested(value: CellValue): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

export function CellDetail({ column, value, rowIndex, onClose }: CellDetailProps): JSX.Element {
  const [copied, setCopied] = useState<string | null>(null);
  const cls = classifyType(column.type);
  const nested = cls === 'nested';
  const parsed = useMemo(() => (nested ? parseNested(value) : value), [nested, value]);
  const formatted = formatCell(value, column.type);

  const announce = async (text: string, label: string): Promise<void> => {
    const ok = await copyText(text);
    setCopied(ok ? label : 'Copy blocked by the browser');
    window.setTimeout(() => setCopied(null), 1600);
  };

  return (
    <aside
      className="flex w-80 shrink-0 flex-col border-l border-line bg-surface"
      aria-label="Cell detail"
      data-testid="cell-detail"
    >
      <div className="panel-head">
        <span className="eyebrow">Cell</span>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Close cell detail">
          ✕
        </button>
      </div>

      <div className="border-b border-line px-2.5 py-2">
        <div className="truncate font-mono text-xs text-ink">{column.name}</div>
        <div className="mt-0.5 flex items-center gap-2">
          <span className="eyebrow">{column.typeText}</span>
          <span className="tnum text-[10px] text-faint">row {rowIndex + 1}</span>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-2.5">
        {isSqlNull(value) ? (
          <p className="v-null font-mono text-xs">
            NULL
            <span className="ml-2 not-italic text-faint">— no value, distinct from &quot;&quot;</span>
          </p>
        ) : nested ? (
          <JsonTree
            value={parsed}
            path=""
            onCopyPath={(path) => void announce(path, `Copied path ${path}`)}
            onCopyValue={(text) => void announce(text, 'Copied value')}
          />
        ) : formatted.kind === 'empty' ? (
          <p className="v-empty font-mono text-xs">
            &quot;&quot;
            <span className="ml-2 text-faint">— empty string, length 0</span>
          </p>
        ) : (
          <pre className="whitespace-pre-wrap break-all font-mono text-xs leading-5 text-ink">
            {formatted.full}
          </pre>
        )}
      </div>

      <div className="flex flex-wrap gap-1 border-t border-line p-2">
        <button
          type="button"
          className="btn"
          onClick={() => void announce(renderableText(value), 'Copied value')}
        >
          Copy value
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => void announce(toSqlLiteral(value, column.type), 'Copied SQL literal')}
        >
          Copy as SQL
        </button>
        <button
          type="button"
          className="btn"
          onClick={() =>
            void announce(
              `"${column.name}" = ${toSqlLiteral(value, column.type)}`,
              'Copied predicate',
            )
          }
        >
          Copy predicate
        </button>
      </div>

      <div className="h-5 px-2.5 text-[10px] text-local" role="status" aria-live="polite">
        {copied}
      </div>
    </aside>
  );
}
